import { Link } from 'react-router-dom';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import SectionHeading from '@/components/base/SectionHeading';
import WorkCard from '@/components/feature/WorkCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useRecentViews } from '@/hooks/useRecentViews';
import { getLatestWorks, getPopularWorks, getWorkById } from '@/lib/works';
import { categoryEntries, siteStats } from '@/mocks/homeContent';

const CONTAINER = 'mx-auto w-full max-w-[1240px] px-4 md:px-8 lg:px-10';

export default function Home() {
  usePageMeta(
    '日韩中文字幕-最新日韩影视作品中文字幕资源汇总',
    '日韩中文字幕网站，提供丰富的日韩影视作品中文字幕信息，涵盖热门电影、电视剧及综艺节目，帮助用户方便查找和了解相关影视资源。'
  );

  const latest = getLatestWorks(8);
  const popular = getPopularWorks(6);
  const { recentIds } = useRecentViews();
  const recentWorks = recentIds
    .map((id) => getWorkById(id))
    .filter((work): work is NonNullable<typeof work> => Boolean(work));

  return (
    <>
      <HeroCarousel />

      <section className={`${CONTAINER} py-14 md:py-20`}>
        <SectionHeading
          title="分类快捷入口"
          subtitle="按作品类型快速直达，找到你想看的日韩影视中文字幕"
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
          {categoryEntries.map((entry) => (
            <Link
              key={entry.type}
              to={`/category/${entry.type}`}
              className="group relative flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50"
            >
              <div className="relative h-44 w-full overflow-hidden bg-background-200">
                <img
                  src={entry.image}
                  alt={`${entry.title}分类入口`}
                  title={`${entry.title} - 日韩中文字幕`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 to-transparent"></div>
                <span className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center rounded-md bg-background-50/90 text-primary-600">
                  <i className={`${entry.icon} text-xl`}></i>
                </span>
                <div className="absolute bottom-4 left-4">
                  <p className="font-heading text-xl font-bold text-background-50">{entry.title}</p>
                  <p className="text-xs uppercase tracking-widest text-background-200">{entry.subtitle}</p>
                </div>
              </div>
              <div className="flex flex-1 items-center justify-between gap-3 p-4">
                <p className="text-sm leading-relaxed text-foreground-600">{entry.description}</p>
                <i className="ri-arrow-right-line text-xl text-foreground-300 transition-colors group-hover:text-primary-500"></i>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-20">
        <div className={CONTAINER}>
          <SectionHeading
            title="最新字幕资源"
            subtitle="按字幕更新时间排序，掌握最新上架的日韩影视中文字幕"
            moreTo="/category/movie"
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
            {latest.map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
        </div>
      </section>

      <section className={`${CONTAINER} py-14 md:py-20`}>
        <SectionHeading
          title="热门推荐"
          subtitle="本月热度最高的日韩影视作品与字幕资源"
          moreTo="/search"
          moreLabel="探索更多"
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6 md:gap-5">
          {popular.map((work, i) => (
            <div key={work.id} className="relative">
              <span className="absolute -left-1 -top-1 z-10 flex h-7 w-7 items-center justify-center rounded-md bg-accent-500 font-heading text-sm font-bold text-background-50">
                {i + 1}
              </span>
              <WorkCard work={work} />
            </div>
          ))}
        </div>
      </section>

      {recentWorks.length > 0 && (
        <section className="bg-background-100 py-14 md:py-20">
          <div className={CONTAINER}>
            <SectionHeading
              title="最近浏览"
              subtitle="你在本站查看过的影视作品，记录保存在本地浏览器"
            />
            <div className="flex gap-4 overflow-x-auto pb-2 no-scrollbar">
              {recentWorks.map((work) => (
                <div key={work.id} className="w-44 shrink-0 md:w-52">
                  <WorkCard work={work} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className={`${CONTAINER} py-14 md:py-20`}>
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-14">
          <div className="relative overflow-hidden rounded-lg border border-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=Stylized%20artistic%20illustration%20of%20a%20cozy%20home%20theater%20room%20with%20a%20glowing%20screen%20and%20floating%20subtitle%20text%20lines%2C%20deep%20indigo%20and%20warm%20amber%20gradient%2C%20soft%20ambient%20light%2C%20no%20readable%20text%2C%20elegant%20editorial%20composition%2C%20high%20detail%2C%20premium%20aesthetic&width=900&height=720&seq=home-intro-01&orientation=landscape"
              alt="日韩中文字幕网站站点介绍示意图"
              title="日韩中文字幕 - 影视字幕信息平台"
              className="h-full w-full object-cover"
            />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              为中文观众打造的日韩影视字幕资料库
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              我们汇集了来自多家合作字幕组的日韩影视中文字幕信息，覆盖电影、电视剧与综艺节目。你可以在本站快速检索作品、筛选地区与年份、查看字幕版本详情，并收藏感兴趣的内容。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              本站仅提供字幕信息展示与索引服务，不提供影视文件与字幕文件下载，所有内容均用于学习与交流参考。
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {siteStats.map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-200 bg-background-100 p-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className={`${stat.icon} text-lg`}></i>
                  </span>
                  <p className="mt-3 font-heading text-xl font-bold text-foreground-950">{stat.value}</p>
                  <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/category/movie"
                className="inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                浏览电影字幕
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/about"
                className="inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                了解我们
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}